import React from 'react';
import { useAuthStore, useStores } from '../../../../store';
import { useForm, Spinner, BasicFormWithAlerts, notification } from 'ui-lib';

import { DeviceGroupSelectItem, DeviceIdentifierItem } from '../../components/device-groups-select';
import { useDeviceGroupCommonStates } from 'pages/private/devices/device-groups';
import { OrganisationSelectFormItem } from '../../components/organisation-component';
import { observer } from 'mobx-react';
import fileTransferCss from './file-transfer.css';
import { DeviceUuidHyperlink } from 'pages/private/components/device-uuid-hyperlink-component';
import { DeviceGroupHyperlink } from 'pages/private/components/device-group-hyperlink-component';
import { DeviceGroupDto } from 'dto/device-group-dto';
import { useAlertMessages } from 'pages/private/components/alert/use-messages';
import { isNullOrEmpty } from 'common-utils';

export interface ISendCommandSubmitFormValues {
    varOrClientAccountId: number;
    deviceGroupName?: string;
    deviceName?: string;
    formValues: any;
    enableReferenceLink: (deviceUid?: string) => void;
}
interface ISendCommandFormOptions {
    onSubmit: (options: ISendCommandSubmitFormValues) => Promise<any>;
    deviceGroupName?: string;
    uid?: string;
    extra?: React.ReactNode;
    extraFormItems?: React.ReactNode[];
    loading?: boolean;
}

enum ACTIONS {
    DEVICE_GROUP = 'DEVICE_GROUP',
    DEVICE = 'DEVICE',
}
export interface ISelectableDeviceGroupStates {
    value?: string;
    type?: ACTIONS;
}
interface StateProps {
    deviceGroupName?: string;
    deviceName?: string;
}
const useSendCommand = (options?: StateProps) => {
    const {
        treeSelectableDataSource: deviceGroupsTree,
        deviceGroupLoading,
        onAccountChanged,
    } = useDeviceGroupCommonStates({
        renderNode: (group: DeviceGroupDto) =>({
            disabled: group.deviceGroupType === 'isClient',
        })
    });
    const { warningMessages, addWarningMessages, clearWarningMessages } = useAlertMessages();
    const commonStore = useStores().commonStore;

    // The device name is higher priority
    const [component, setComponent] = React.useState<ISelectableDeviceGroupStates>({
        value: options?.deviceName || options?.deviceGroupName,
        type: options?.deviceName ? ACTIONS.DEVICE : ACTIONS.DEVICE_GROUP,
    });

    const deviceName = React.useMemo(() => {
        if (component.type !== ACTIONS.DEVICE) {
            return null;
        }
        return component.value;
    }, [component]);

    const deviceGroupName = React.useMemo(() => {
        if (component.type !== ACTIONS.DEVICE_GROUP) {
            return null;
        }
        return component.value;
    }, [component]);

    React.useEffect(() => {
        clearWarningMessages();
        if (!isNullOrEmpty(deviceGroupName)) {
            addWarningMessages('Running the shell command on a device group with many devices may take a significant amount of time to complete.');
            return;
        }
    }, [deviceGroupName]);

    const [sendCommandLinkState, setSendCommandLinkState] = React.useState<{
        enable: boolean;
        linkName: string;
    }>({
        enable: false,
        linkName: '',
    });

    const deviceGroupToLink = React.useMemo(() => {
        if (component.type !== ACTIONS.DEVICE_GROUP || !sendCommandLinkState?.enable) {
            return null;
        }
        // We already have the value of the device group, no need to get value from the link name
        return deviceGroupName;
    }, [component, sendCommandLinkState]);

    const deviceToLink = React.useMemo(() => {
        if (component.type !== ACTIONS.DEVICE || !sendCommandLinkState?.enable) {
            return null;
        }
        return sendCommandLinkState.linkName;
    }, [component, sendCommandLinkState]);

    // Trigger account change to rebuild the device group tree
    React.useEffect(() => {
        window.history.replaceState({}, document.title);
        if (!commonStore.selectedOrganisation) return;
        onAccountChanged(commonStore.selectedOrganisation);
    }, [commonStore.selectedOrganisation]);

    React.useEffect(() => {
        // Reset the link state whenever the device name or device group name changes
        setSendCommandLinkState({
            enable: false,
            linkName: '',
        });
    }, [deviceGroupName, deviceName]);

    return {
        deviceGroupsTree,
        deviceName,
        onDeviceNameChange: (value: string) => {
            setComponent({ value, type: ACTIONS.DEVICE });
        },
        loading: deviceGroupLoading,
        deviceGroupName,
        varOrCLientAccountId: commonStore.selectedOrganisation,
        deviceGroupToLink,
        deviceToLink,
        enableReferenceLink: (deviceUid?: string) => {
            setSendCommandLinkState({
                enable: true,
                linkName: deviceUid,
            });
        },
        onDeviceGroupChange: (value: string) => {
            setComponent({ value, type: ACTIONS.DEVICE_GROUP });
        },
        warningMessages,
    };
}

export const SendCommandForm = observer((options: ISendCommandFormOptions) => {
    const { currentUser } = useAuthStore();
    const stores = useStores();
    const [form] = useForm();
    const { onSubmit, deviceGroupName, uid, loading = false } = options;
    const sendCommandStates = useSendCommand({
        deviceGroupName: deviceGroupName,
        deviceName: uid,
    });
    React.useEffect(() => {
        form.setFieldsValue({
            'device-identifier-item': sendCommandStates.deviceName ?? '',
        });
        form.resetFields(['device-group-item']);
    }, [sendCommandStates.deviceName, sendCommandStates.deviceGroupName, sendCommandStates.varOrCLientAccountId]);

    return (
        <Spinner spinning={sendCommandStates.loading || loading} className={fileTransferCss.spinner}>
            <BasicFormWithAlerts
                form={form}
                className={fileTransferCss['transfer-form']}
                warningMessages={sendCommandStates.warningMessages}
                onSubmit={async (values) => {
                    if (sendCommandStates.deviceGroupName && sendCommandStates.deviceName) {
                        return notification['error']({
                            message: 'Cannot use the device, device group field at the same time.',
                            placement: 'topRight',
                            duration: 10,
                        });
                    }
                    await onSubmit({
                        varOrClientAccountId: sendCommandStates.varOrCLientAccountId,
                        deviceGroupName: sendCommandStates.deviceGroupName,
                        deviceName: sendCommandStates.deviceName,
                        formValues: values,
                        enableReferenceLink: sendCommandStates.enableReferenceLink,
                    });
                }}
                items={[
                    <OrganisationSelectFormItem
                        key='organisation'
                        code='organisation'
                        isRequired={true}
                        labelAlign='right'
                        accountType={currentUser.accountType}
                        onChange={(value) => {
                            sendCommandStates.onDeviceGroupChange(undefined);
                        }}
                    />,
                    <DeviceIdentifierItem
                        key='device-identifier-item'
                        code='device-identifier-item'
                        initialValue={sendCommandStates.deviceName}
                        colon={true}
                        labelAlign='right'
                        onChange={(value) => {
                            sendCommandStates.onDeviceNameChange(value.target.value);
                        }}
                    />,
                    <DeviceGroupSelectItem
                        code='device-group-item'
                        label='Device group'
                        colon={true}
                        labelAlign='right'
                        initialValue={sendCommandStates.deviceGroupName}
                        selectProps={{
                            dataSource: sendCommandStates.deviceGroupsTree,
                            expandAll: true,
                            allowClear: true,
                            value: sendCommandStates.deviceGroupName,
                            onChange: (value) => {
                                sendCommandStates.onDeviceGroupChange(value);
                            },
                        }}
                    />,
                    ...(options.extraFormItems || []),
                ]}
            />
            {options?.extra && <div className={fileTransferCss['extra']}>{options.extra}</div>}
            {sendCommandStates.deviceToLink && (
                <DeviceUuidHyperlink
                    uid={sendCommandStates.deviceToLink}
                    type='primary'
                    label='Go to Device Details'
                    accountId={stores.commonStore.selectedOrganisation}
                />
            )}
            {sendCommandStates.deviceGroupToLink && (
                <DeviceGroupHyperlink deviceGroupName={sendCommandStates.deviceGroupToLink} />
            )}
        </Spinner>
    );
});
